// File: src/hooks/useVideoGeneration.tsx

import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { VideoGeneration, AIService, PromptTemplate, VideoGenerationCreate } from '../types';
import { startVideoPromptGeneration, startVideoGenerationFromPrompt } from '../services/api';
import { getSingleVideoGeneration } from '../services/api';

interface UseVideoGenerationProps {
    selectedProductId: number | '';
    selectedAIServiceCode: string;
    selectedVideoServiceCode: string;
    additionalInstructions: string;
    selectedPromptTemplateId: number | '';
    selectedReferenceImageUrl: string | null;
    allAiServices?: AIService[];
    promptTemplates?: PromptTemplate[];
    currentGeneration?: VideoGeneration | null;
}

export const useVideoGeneration = ({
                                       selectedProductId,
                                       selectedAIServiceCode,
                                       selectedVideoServiceCode,
                                       selectedPromptTemplateId,
                                       additionalInstructions,
                                       selectedReferenceImageUrl,
                                       allAiServices,
                                       promptTemplates,
                                       currentGeneration,
                                   }: UseVideoGenerationProps) => {
    const [generationId, setGenerationId] = useState<number | null>(null);
    const [generationStep, setGenerationStep] = useState('idle');
    const [error, setError] = useState<string | null>(null);
    const [statusText, setStatusText] = useState<string | null>(null);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const selectedPromptTemplate = useMemo(() => promptTemplates?.find(t => t.id === selectedPromptTemplateId), [promptTemplates, selectedPromptTemplateId]);
    const selectedAIService = useMemo(() => allAiServices?.find(s => s.code === selectedAIServiceCode), [allAiServices, selectedAIServiceCode]);
    const selectedVideoService = useMemo(() => allAiServices?.find(s => s.code === selectedVideoServiceCode), [allAiServices, selectedVideoServiceCode]);

    const promptGenMutation = useMutation({
        mutationFn: () => {
            if (!selectedProductId || !selectedReferenceImageUrl) throw new Error("Product and Reference Image must be selected.");
            const requestData: VideoGenerationCreate = {
                product_id: selectedProductId,
                ai_service_used: selectedAIServiceCode,
                video_ai_service_used: selectedVideoServiceCode,
                prompt_template_id: selectedPromptTemplateId as number,
                language: 'ru',
                extra_instructions: additionalInstructions,
                reference_image_url: selectedReferenceImageUrl,
            };
            return startVideoPromptGeneration(selectedProductId, requestData);
        },
        onSuccess: (data) => {
            setGenerationId(data.id);
            setGenerationStep(data.status);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ['videoGenerationHistory', selectedProductId] });
        },
        onError: (e: any) => {
            setError(e.message || 'Failed to start prompt generation');
            setGenerationStep('prompt_failed');
        },
    });

    const videoGenMutation = useMutation({
        mutationFn: () => {
            if (!selectedProductId || !generationId) throw new Error("Product and Generation ID are required.");
            // ✅ ИСПРАВЛЕНИЕ: Откатываем изменение. Больше не передаем тело запроса.
            return startVideoGenerationFromPrompt(selectedProductId, generationId);
        },
        onSuccess: (data) => {
            setGenerationStep(data.status);
            setError(null);
        },
        onError: (e: any) => {
            setError(e.message || 'Failed to start video generation');
            setGenerationStep('video_failed');
        },
    });

    useEffect(() => {
        if (!selectedProductId || !generationId) return;
        if (!['prompt_running', 'video_running'].includes(generationStep)) return;

        let isActive = true;

        const pollStatus = async () => {
            try {
                const result = await getSingleVideoGeneration(selectedProductId, generationId);
                if (!isActive) return;
                setGenerationStep(result.status);
                if (result.error_message) setError(result.error_message);
                if (['completed', 'video_failed', 'prompt_failed'].includes(result.status)) {
                    isActive = false;
                }
            } catch (err: any) {
                if (isActive) setError(err.message || 'Polling failed');
            }
        };

        const intervalId = setInterval(pollStatus, 3000);
        pollStatus(); // immediate first check

        return () => {
            isActive = false;
            clearInterval(intervalId);
        };
    }, [selectedProductId, generationId, generationStep]);

    const resetGeneration = () => {
        setGenerationId(null);
        setGenerationStep('idle');
        setError(null);
    };

    const handleLoadGeneration = (gen: VideoGeneration) => {
        setGenerationId(gen.id);
        setGenerationStep(gen.status);
        setError(gen.error_message || null);
    };

    const canStartPromptGen = useMemo(() => {
        const isReadyForPromptGen = (generationStep === 'idle' || generationStep === 'prompt_failed');
        return isReadyForPromptGen &&
            selectedProductId !== '' &&
            selectedAIServiceCode !== '' &&
            selectedVideoServiceCode !== '' &&
            selectedPromptTemplateId !== '' &&
            selectedReferenceImageUrl !== null;
    }, [generationStep, selectedProductId, selectedAIServiceCode, selectedVideoServiceCode, selectedPromptTemplateId, selectedReferenceImageUrl]);

    const canStartVideoGen = useMemo(() => {
        const isReadyForVideoGen = (generationStep === 'prompt_ready' || generationStep === 'video_failed');
        return isReadyForVideoGen &&
            !!generationId;
    }, [generationStep, generationId]);

    const startVideoGenerationFlow = async () => {
        if (!selectedProductId || !selectedReferenceImageUrl) {
            setError("Product and reference image are required.");
            return;
        }
        setStatusText('Starting video generation...');
        setGeneratedVideoUrl(null);
        setError(null);

        try {
            setGenerationStep('prompt_running');
            const requestData: VideoGenerationCreate = {
                product_id: selectedProductId,
                ai_service_used: selectedAIServiceCode,
                video_ai_service_used: selectedVideoServiceCode,
                prompt_template_id: selectedPromptTemplateId as number,
                language: 'ru',
                extra_instructions: additionalInstructions,
                reference_image_url: selectedReferenceImageUrl,
            };

            const promptResp = await startVideoPromptGeneration(selectedProductId, requestData);
            setGenerationId(promptResp.id);
            setStatusText('Generating prompt...');
            let status = promptResp.status;

            while (true) {
                const result = await getSingleVideoGeneration(selectedProductId, promptResp.id);
                status = result.status;
                if (status === 'prompt_ready') break;
                if (status.endsWith('_failed')) throw new Error('Prompt generation failed');
                await new Promise(r => setTimeout(r, 2500));
            }

            setStatusText('Prompt ready. Starting video rendering...');
            await startVideoGenerationFromPrompt(selectedProductId, promptResp.id);
            setGenerationStep('video_running');

            while (true) {
                const result = await getSingleVideoGeneration(selectedProductId, promptResp.id);
                status = result.status;
                if (result.video_result_details?.video_url) {
                    setGeneratedVideoUrl(result.video_result_details.video_url);
                }
                if (status === 'completed') break;
                if (status.endsWith('_failed')) throw new Error('Video generation failed');
                await new Promise(r => setTimeout(r, 3000));
            }

            setStatusText('Completed ✅');
            setGenerationStep('completed');
        } catch (err: any) {
            setError(err.message || 'Generation failed');
            setStatusText('Generation failed ❌');
            setGenerationStep('video_failed');
        }
    };

    return {
        generationId,
        generationStep,
        error,
        promptGenMutation,
        videoGenMutation,
        resetGeneration,
        handleLoadGeneration,
        canStartPromptGen,
        canStartVideoGen,
        selectedPromptTemplate,
        selectedAIService,
        selectedVideoService,
        statusText,
        generatedVideoUrl,
        startVideoGenerationFlow,
    };
};